<script setup>
// SingleSelect.vue
import { defineModel, computed, ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementBounding, useWindowSize } from '@vueuse/core';
import { picoSearch } from '@scmmishra/pico-search';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';

const {
  options,
  disableSearch,
  placeholderIcon,
  placeholder,
  placeholderTrailingIcon,
  searchPlaceholder,
} = defineProps({
  options: {
    type: Array,
    required: true,
  },
  disableSearch: {
    type: Boolean,
    default: false,
  },
  placeholderIcon: {
    type: String,
    default: 'i-lucide-plus',
  },
  placeholder: {
    type: String,
    default: '',
  },
  placeholderTrailingIcon: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const selected = defineModel({
  type: Object,
  required: true,
});

const isOpen = ref(false);
const triggerRef = ref(null);
const searchTerm = ref('');

const { top, left, width, bottom } = useElementBounding(triggerRef);
const { height: windowHeight } = useWindowSize();

const dropdownStyle = computed(() => {
  const DROPDOWN_HEIGHT = 320;
  const spaceBelow = windowHeight.value - bottom.value;
  const openUp = spaceBelow < DROPDOWN_HEIGHT && top.value > DROPDOWN_HEIGHT;

  return {
    position: 'fixed',
    left: `${left.value}px`,
    minWidth: `${Math.max(width.value, 224)}px`,
    zIndex: 9999999,
    ...(openUp
      ? { bottom: `${windowHeight.value - top.value}px` }
      : { top: `${bottom.value + 4}px` }
    ),
  };
});

const searchResults = computed(() => {
  if (!options) return [];
  return picoSearch(options, searchTerm.value, ['name']);
});

const selectedItem = computed(() => {
  if (!options) return null;
  if (!selected.value) return null;

  const optionToSearch = Array.isArray(selected.value)
    ? selected.value[0]
    : selected.value;
  
  return options.find(option => option.id === optionToSearch.id);
});

const toggleSelected = option => {
  const optionToToggle = {
    id: option.id,
    name: option.name,
  };

  if (selected.value && selected.value.id === optionToToggle.id) {
    selected.value = null;
  } else {
    selected.value = optionToToggle;
  }
  
  close();
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchTerm.value = '';
  }
};

const close = () => {
  isOpen.value = false;
};

// Click outside handler
const handleClickOutside = (event) => {
  if (!isOpen.value) return;
  
  const triggerEl = triggerRef.value?.$el || triggerRef.value;
  const dropdownEl = document.querySelector('[data-singleselect-dropdown]');
  
  if (triggerEl?.contains(event.target)) return;
  if (dropdownEl?.contains(event.target)) return;
  
  close();
};

watch(isOpen, (newValue) => {
  if (newValue) {
    requestAnimationFrame(() => {
      document.addEventListener('click', handleClickOutside);
    });
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="relative inline-block">
    <Button
      v-if="selectedItem"
      ref="triggerRef"
      sm
      slate
      faded
      type="button"
      :icon="selectedItem.icon"
      :label="selectedItem.name"
      @click="toggle"
    />
    <Button
      v-else
      ref="triggerRef"
      sm
      slate
      faded
      type="button"
      :trailing-icon="placeholderTrailingIcon"
      @click="toggle"
    >
      <template #icon>
        <Icon :icon="placeholderIcon" class="text-n-slate-11" />
      </template>
      <span class="text-n-slate-11">{{
        placeholder || t('COMBOBOX.PLACEHOLDER')
      }}</span>
    </Button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        :style="dropdownStyle"
        class="bg-n-solid-2 border border-n-weak rounded-lg shadow-lg singleselect-dropdown"
        data-singleselect-dropdown
      >
        <div v-if="!disableSearch" class="relative p-2 border-b border-n-weak">
          <Icon class="absolute size-4 left-4 top-4" icon="i-lucide-search" />
          <input
            v-model="searchTerm"
            autofocus
            class="p-1.5 pl-8 text-n-slate-11 bg-n-alpha-1 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9"
            :placeholder="searchPlaceholder || t('COMBOBOX.SEARCH_PLACEHOLDER')"
            @click.stop
          />
        </div>
        
        <div class="max-h-80 overflow-y-auto p-1">
          <template v-if="searchResults.length">
            <button
              v-for="option in searchResults"
              :key="option.id"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-3 rounded-md transition-colors cursor-pointer"
              :class="{
                'bg-n-blue-3 text-n-blue-11': selectedItem && selectedItem.id === option.id,
              }"
              @click.stop="toggleSelected(option)"
            >
              <Icon
                v-if="option.icon"
                :icon="option.icon"
                class="flex-shrink-0"
                :class="selectedItem && selectedItem.id === option.id ? 'text-n-blue-9' : 'text-n-slate-11'"
              />
              <span class="flex-1 text-left truncate">{{ option.name }}</span>
              <Icon
                v-if="selectedItem && selectedItem.id === option.id"
                icon="i-lucide-check"
                class="text-n-blue-9 flex-shrink-0"
              />
            </button>
          </template>
          <template v-else-if="searchTerm">
            <div class="px-3 py-8 text-center text-sm text-n-slate-10">
              {{ t('COMBOBOX.EMPTY_SEARCH_RESULTS', { searchTerm: searchTerm }) }}
            </div>
          </template>
          <template v-else>
            <div class="px-3 py-8 text-center text-sm text-n-slate-10">
              {{ t('COMBOBOX.EMPTY_STATE') }}
            </div>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>